import type { Collectible } from '@/config/objects/collectibles';
import {
  Button,
  ConfirmDialog,
  EntityDetailShell,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lightbulb, Package, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const tabs = [{ label: 'Overview', path: 'overview', icon: Lightbulb }] as const;

export function CollectibleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Collectible | null>(null);
  const basePath = `/collectibles/${id}`;

  const deleteMutation = useMutation({
    mutationFn: async (
      collectibleId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/collectibles/${collectibleId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Collectible'] });
        navigate('/collectibles');
      } else {
        throw new Error(response.error || 'Failed to delete collectible');
      }
    },
  });

  const handleDelete = () => {
    if (!pendingDelete) return;
    toast.promise(deleteMutation.mutateAsync(pendingDelete.id), {
      loading: 'Deleting collectible...',
      success: 'Collectible deleted successfully',
      error: (err: Error) => err?.message || 'Failed to delete collectible',
    });
  };

  return (
    <>
      <EntityDetailShell<Collectible>
        resource="collectibles"
        queryKey="Collectible"
        id={id}
        basePath={basePath}
        entityLabel="Collectible"
        tabs={tabs}
        contextKey="collectible"
        hero={{
          category: { label: 'Collectible', icon: Package },
          title: (c) => c?.name || 'Collectible',
          fallbackIcon: Package,
        }}
        breadcrumbs={(c) => [
          { label: 'Home', href: '/' },
          { label: 'Collectible Library', href: '/collectibles' },
          { label: c?.name || 'Collectible', current: true },
        ]}
        headerActions={(c) =>
          c ? (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`${basePath}/edit`)}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setPendingDelete(c);
                  setShowDeleteConfirm(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Collectible"
        description={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
